import { isServiceCategoryId } from '@powerful/config/categories';

import { getSupabaseClient } from '../supabase';

/**
 * Escrita do perfil de consumidor recolhido no onboarding (T1.14).
 *
 * Como em `lib/auth/`, as funções **nunca lançam**: uma exceção que escapa deixa o ecrã preso num
 * spinner. O erro volta como valor e a UI decide o que mostrar.
 *
 * A tabela `consumer_profiles` existe desde a T1.2 e a policy `cprofiles_all_self` já restringe a
 * escrita ao próprio utilizador — esta camada não repõe essa garantia, apenas evita enviar o que a
 * base recusaria de qualquer forma.
 */

export type ConsumerProfileErrorCode =
  | 'validation_error'
  | 'session_expired'
  | 'save_failed'
  | 'network';

export type ConsumerProfileResult<T> =
  | { ok: true; data: T }
  | { ok: false; code: ConsumerProfileErrorCode; message: string };

export interface GeoCoordinates {
  latitude: number;
  longitude: number;
}

export interface ConsumerOnboardingData {
  favoriteCategories: string[];
  city?: string;
  geo?: GeoCoordinates;
  marketingOptIn: boolean;
}

const failure = (
  code: ConsumerProfileErrorCode,
  message: string,
): ConsumerProfileResult<never> => ({ ok: false, code, message });

/**
 * Converte coordenadas em WKT para a coluna `geography(Point, 4326)`.
 *
 * 🔴 **Longitude primeiro.** É a ordem do WKT, e é o contrário da forma como se fala de
 * coordenadas em português. Inverter não dá erro: grava um ponto perfeitamente válido no sítio
 * errado do planeta. Esta função existe isolada precisamente para que esse risco tenha um teste
 * próprio em vez de viver espalhado pelas páginas.
 */
export function toGeoPoint({ latitude, longitude }: GeoCoordinates): string {
  return `POINT(${longitude} ${latitude})`;
}

interface ConsumerProfileRow {
  user_id: string;
  favorite_categories: string[];
  marketing_opt_in: boolean;
  city?: string;
  geo?: string;
}

/**
 * Grava o perfil recolhido no onboarding.
 *
 * É `upsert` porque o onboarding pode ser repetido ou retomado, e porque a linha pode já existir
 * de uma passagem anterior. Chamado mesmo quando a pessoa salta todos os passos (RN-7): o perfil
 * nasce com os defaults, e o shell não precisa de tratar ausência de linha como caso especial.
 */
export async function upsertConsumerProfile(
  data: ConsumerOnboardingData,
): Promise<ConsumerProfileResult<undefined>> {
  const { favoriteCategories, city, geo, marketingOptIn } = data;

  const desconhecida = favoriteCategories.find((id) => !isServiceCategoryId(id));
  if (desconhecida !== undefined) {
    return failure('validation_error', 'Categoria inválida.');
  }

  if (new Set(favoriteCategories).size !== favoriteCategories.length) {
    return failure('validation_error', 'Categorias favoritas não podem repetir-se.');
  }

  try {
    const supabase = getSupabaseClient();
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id;

    if (!userId) {
      return failure('session_expired', 'Sessão expirada.');
    }

    const row: ConsumerProfileRow = {
      user_id: userId,
      favorite_categories: favoriteCategories,
      marketing_opt_in: marketingOptIn,
    };

    // Cidade em branco é ausência de resposta, não uma cidade chamada "". Gravar a string vazia
    // tornaria impossível distinguir "não respondeu" de "respondeu nada".
    const cidade = city?.trim();
    if (cidade) {
      row.city = cidade;
    }

    if (geo) {
      row.geo = toGeoPoint(geo);
    }

    const { error } = await supabase
      .from('consumer_profiles')
      .upsert(row, { onConflict: 'user_id' });

    if (error) {
      // A mensagem crua do Postgres descreve policies e nomes de tabela; não vai para a UI.
      return failure('save_failed', 'Não foi possível guardar as preferências.');
    }

    return { ok: true, data: undefined };
  } catch {
    return failure('network', 'Sem ligação. Tente novamente.');
  }
}

/**
 * Resultado da leitura do marcador. Tipo próprio, fora de `ConsumerProfileErrorCode`: a falha aqui
 * nunca chega ao ecrã — significa só "não redirecionar" (T1.19b, `resolveEntryDestination`).
 */
export type ConsumerProfileReadResult =
  | { ok: true; data: boolean }
  | { ok: false; code: 'session_expired' | 'load_failed' | 'network'; message: string };

/**
 * Existe linha em `consumer_profiles` para a sessão atual? (T1.19b)
 *
 * A linha é o marcador de "onboarding consumer visto": `upsertConsumerProfile` grava-a ao concluir
 * **ou** saltar. 🔴 Erro de leitura volta como erro, nunca como `false` — tratar falha como ausência
 * mandaria para o onboarding quem já o fez (a lição da T1.19 com o rascunho de negócio).
 */
export async function hasConsumerProfile(): Promise<ConsumerProfileReadResult> {
  try {
    const supabase = getSupabaseClient();
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id;
    if (!userId) return { ok: false, code: 'session_expired', message: 'Sessão expirada.' };

    const { data, error } = await supabase
      .from('consumer_profiles')
      .select('user_id')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) return { ok: false, code: 'load_failed', message: 'Não foi possível ler o perfil.' };
    return { ok: true, data: data !== null };
  } catch {
    return { ok: false, code: 'network', message: 'Sem ligação. Tente novamente.' };
  }
}
